import React, { useMemo } from "react";

import Decimal from "decimal.js";

import { Holding } from "@fartherfinance/frontend/api/Investment/hooks/useGetProposalPostTradeHoldings";
import { TLHSwapAccepted } from "@fartherfinance/frontend/api/Investment/types";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";

import { toSortable } from "@src/multiCustodian/components/Client/Accounts/Holdings/Components/BreakdownTable/HoldingsTable";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import FullDataTable from "@src/sharedComponents/Table/FullDataTable";
import { Cell, Row } from "@src/sharedComponents/Table/Types";
import Text from "@src/sharedComponents/Typography/Text";

import { ESAndListLookup } from "./createEquivalentSecurityAndEsListLookup";
import SecurityCell from "./SecurityCell";

type SecurityDb = Awaited<
  ReturnType<typeof useSecuritiesBySecurityIds>
>["data"];

type PostTradeHoldingsColumns =
  | "Account"
  | "Ticker"
  | "Pre-Qty"
  | "Post-Qty"
  | "Pre-Wt"
  | "Post-Wt"
  | "Pre-Mkt"
  | "Post-Mkt"
  | "Price"
  | "Port-Wt"
  | "Port-Dlt";

interface Props {
  securityDb: SecurityDb | undefined;
  postTradeHoldings: Holding[] | undefined;
  excludedAssets?: Array<{ securityId: string; createdAt?: Date }>;
  acceptedTLHSwaps: TLHSwapAccepted[];
  hasError: boolean;
  esLookup?: ESAndListLookup;
}

const PostTradeHoldings: React.FC<Props> = (props) => {
  const postTradeHoldings = useMemo(():
    | Row<PostTradeHoldingsColumns, Cell>[]
    | undefined => {
    // Edge case: If we have no post trade holdings then we return an empty array. SecurityDb is undefined in this case.
    if (
      props.postTradeHoldings !== undefined &&
      props.postTradeHoldings.length === 0
    ) {
      return [];
    }

    if (
      props.postTradeHoldings === undefined ||
      props.securityDb === undefined
    ) {
      return undefined;
    }

    const isCash = props.securityDb.find((x) =>
      x.identifiers.some(
        (y) => y.registrar === "TICKER" && y.identifier === "US DOLLARS"
      )
    );

    return props.postTradeHoldings.map(
      (holding): Row<PostTradeHoldingsColumns, Cell> => {
        const esListMatch =
          props.esLookup !== undefined
            ? props.esLookup.lists[
                props.esLookup.securities[holding.securityId]
              ]
            : undefined;

        const esPillData =
          esListMatch !== undefined && esListMatch.matches.length >= 2
            ? {
                listIndex: esListMatch.listIndex,
                listName: esListMatch.listName,
              }
            : undefined;

        return {
          key: `${holding.physicalAccountId}-${holding.securityId}`,
          Account: holding.custodianAccountId,
          Ticker: {
            value: (
              <SecurityCell
                securityId={holding.securityId}
                securityDb={props.securityDb}
                isDoNotSell={props.excludedAssets?.some(
                  (asset) => asset.securityId === holding.securityId
                )}
                isTaxLossHarvest={
                  props.acceptedTLHSwaps.some(
                    (swap) =>
                      swap.originalSecurity === holding.securityId ||
                      swap.replacementSecurity === holding.securityId
                  ) ?? false
                }
                equivalentSecurityPillData={esPillData}
              />
            ),
            fullValue:
              holding.securityId === isCash?.id
                ? "@" // Cash is always on top
                : props.securityDb
                    ?.find((x) => x.id === holding.securityId)
                    ?.identifiers.find((x) => x.registrar === "TICKER")
                    ?.identifier ?? "ZZZZ",
          },
          "Pre-Qty": {
            value:
              holding.securityId === isCash?.id
                ? holding.preQuantity.toNumber().toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })
                : holding.preQuantity.toNumber().toLocaleString("en-US"),
            fullValue: toSortable(holding.preQuantity.toNumber()),
          },
          "Post-Qty": {
            value:
              holding.securityId === isCash?.id
                ? holding.postQuantity.toNumber().toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })
                : holding.postQuantity.toNumber().toLocaleString("en-US"),
            fullValue: toSortable(holding.postQuantity.toNumber()),
          },
          "Pre-Wt": {
            value: holding.preWeight.toNumber().toLocaleString("en-US", {
              style: "percent",
              minimumFractionDigits: 2,
            }),
            fullValue: toSortable(holding.preWeight.toNumber(), 10),
          },
          "Post-Wt": {
            value: holding.postWeight.toNumber().toLocaleString("en-US", {
              style: "percent",
              minimumFractionDigits: 2,
            }),
            fullValue: toSortable(holding.postWeight.toNumber(), 10),
          },
          "Pre-Mkt": {
            value: holding.preMarketValue.toNumber().toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            fullValue: toSortable(holding.preMarketValue.toNumber(), 2),
          },
          "Post-Mkt": {
            value: holding.postMarketValue.toNumber().toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            fullValue: toSortable(holding.postMarketValue.toNumber(), 2),
          },
          Price: {
            value: holding.asOfPrice.toNumber().toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            fullValue: toSortable(holding.asOfPrice.toNumber(), 2),
          },
          "Port-Wt": {
            value: holding.portfolioWeight.toNumber().toLocaleString("en-US", {
              style: "percent",
              minimumFractionDigits: 2,
            }),
            fullValue: toSortable(holding.portfolioWeight.toNumber(), 10),
          },
          "Port-Dlt": {
            value: holding.portfolioWeightDelta
              .toNumber()
              .toLocaleString("en-US", {
                style: "percent",
                minimumFractionDigits: 2,
              }),
          },
        };
      }
    );
  }, [
    props.postTradeHoldings,
    props.securityDb,
    props.excludedAssets,
    props.acceptedTLHSwaps,
    props.esLookup,
  ]);

  const totalsRow = useMemo(():
    | Row<PostTradeHoldingsColumns, Cell>
    | undefined => {
    if (props.postTradeHoldings === undefined) {
      return undefined;
    }

    const totals = props.postTradeHoldings.reduce(
      (acc, row) => {
        return {
          preQuantity: acc.preQuantity.plus(row.preQuantity),
          postQuantity: acc.postQuantity.plus(row.postQuantity),
          preMarketValue: acc.preMarketValue.plus(row.preMarketValue),
          postMarketValue: acc.postMarketValue.plus(row.postMarketValue),
          preWeight: acc.preWeight.plus(row.preWeight),
          postWeight: acc.postWeight.plus(row.postWeight),
          portfolioWeight: acc.portfolioWeight.plus(row.portfolioWeight),
        };
      },
      {
        preQuantity: new Decimal(0),
        postQuantity: new Decimal(0),
        preMarketValue: new Decimal(0),
        postMarketValue: new Decimal(0),
        preWeight: new Decimal(0),
        postWeight: new Decimal(0),
        portfolioWeight: new Decimal(0),
      }
    );

    return {
      key: "totals",
      Account: {
        value: <Text variant="bodyBold">Totals</Text>,
        fullValue: "ZZZZZZZ", // always last
      },
      Ticker: {
        value: "-",
        fullValue: "ZZZZZZZ",
      },
      "Pre-Qty": {
        value: "-",
        fullValue: "ZZZZZZZ",
      },
      "Post-Qty": {
        value: "-",
        fullValue: "ZZZZZZZ",
      },
      "Pre-Wt": {
        value: (
          <Text variant="bodyBold">
            {totals.preWeight.toNumber().toLocaleString("en-US", {
              style: "percent",
              minimumFractionDigits: 2,
            })}
          </Text>
        ),
        fullValue: toSortable(totals.preWeight.toNumber(), 10),
      },
      "Post-Wt": {
        value: (
          <Text variant="bodyBold">
            {totals.postWeight.toNumber().toLocaleString("en-US", {
              style: "percent",
              minimumFractionDigits: 2,
            })}
          </Text>
        ),
        fullValue: toSortable(totals.postWeight.toNumber(), 10),
      },
      "Pre-Mkt": {
        value: (
          <Text variant="bodyBold">
            {totals.preMarketValue.toNumber().toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </Text>
        ),
        fullValue: toSortable(totals.preMarketValue.toNumber(), 2),
      },
      "Post-Mkt": {
        value: (
          <Text variant="bodyBold">
            {totals.postMarketValue.toNumber().toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </Text>
        ),
        fullValue: toSortable(totals.postMarketValue.toNumber(), 2),
      },
      "Port-Wt": {
        value: (
          <Text variant="bodyBold">
            {totals.portfolioWeight.toNumber().toLocaleString("en-US", {
              style: "percent",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        ),
        fullValue: toSortable(totals.portfolioWeight.toNumber(), 2),
      },
      "Port-Dlt": {
        value: "-",
        fullValue: "ZZZZZZZ",
      },
      Price: {
        value: "-",
        fullValue: "ZZZZZZZ",
      },
    };
  }, [props.postTradeHoldings]);

  const allRows = useMemo(() => {
    if (postTradeHoldings === undefined || totalsRow === undefined) {
      return undefined;
    }

    return [...postTradeHoldings, totalsRow];
  }, [postTradeHoldings, totalsRow]);

  if (props.hasError) {
    return (
      <AlertInline
        severity="error"
        message="Error loading post-trade holdings"
      />
    );
  }

  if (allRows === undefined) {
    return (
      <FullDataTable<PostTradeHoldingsColumns, Cell>
        key={"post-trade-holdings-loading"}
        useQueryParams={false}
        columns={[
          "Account",
          "Ticker",
          "Pre-Qty",
          "Post-Qty",
          "Pre-Wt",
          "Post-Wt",
          "Pre-Mkt",
          "Post-Mkt",
          "Price",
        ]}
        rows={Array.from({ length: 10 }).map((_, idx) => ({
          key: idx.toString(),
          Account: { value: <Skeleton width={100} />, fullValue: "" },
          Ticker: { value: <Skeleton width={100} />, fullValue: "" },
          "Pre-Qty": { value: <Skeleton width={100} />, fullValue: "" },
          "Post-Qty": { value: <Skeleton width={100} />, fullValue: "" },
          "Pre-Wt": { value: <Skeleton width={100} />, fullValue: "" },
          "Post-Wt": { value: <Skeleton width={100} />, fullValue: "" },
          "Pre-Mkt": { value: <Skeleton width={100} />, fullValue: "" },
          "Post-Mkt": { value: <Skeleton width={100} />, fullValue: "" },
          "Port-Wt": { value: <Skeleton width={100} />, fullValue: "" },
          "Port-Dlt": { value: <Skeleton width={100} />, fullValue: "" },
          Price: { value: <Skeleton width={100} />, fullValue: "" },
        }))}
        defaultSortColumn={["Account", "asc"]}
        secondaryDefaultSortColumn={["Ticker", "asc"]}
        emptyCell={<Skeleton />}
        hidePagination
        defaultRowsPerPage={10}
      />
    );
  }

  if (postTradeHoldings !== undefined && postTradeHoldings.length === 0) {
    return <AlertInline severity="warning" message="No post-trade holdings" />;
  }

  return (
    <FullDataTable<PostTradeHoldingsColumns, Cell>
      key={"post-trade-holdings-loaded"}
      useQueryParams={false}
      columns={[
        "Account",
        "Ticker",
        "Pre-Qty",
        "Post-Qty",
        "Pre-Wt",
        "Post-Wt",
        "Port-Wt",
        "Port-Dlt",
        "Pre-Mkt",
        "Post-Mkt",
        "Price",
      ]}
      rows={allRows}
      defaultSortColumn={["Account", "asc"]}
      secondaryDefaultSortColumn={["Ticker", "asc"]}
      emptyCell={<Skeleton />}
      hidePagination
      defaultRowsPerPage={allRows.length}
    />
  );
};

export default PostTradeHoldings;
